import Layout from "@/components/layout/Layout";
import Button from "@/components/ui/Button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTrigger,
} from "@/components/ui/Dialog";
import Spinner from "@/components/ui/Spinner";
import TextareaField from "@/components/ui/Textareafield";
import TextField from "@/components/ui/Textfield";
import useAuthStore from "@/store/authStore";
import { DialogTitle } from "@radix-ui/react-dialog";
import React, { useEffect, useState } from "react";
import { BiSearch, BiSend, BiUser } from "react-icons/bi";
import { BsPlus } from "react-icons/bs";
import useManagePeopleStore from "./store/managePeople";
import { useShallow } from "zustand/react/shallow";
import { toTitleCase } from "@/helper/utils";
import Dropdown from "@/components/ui/DropDown";
import useGlobalStore from "@/store/globalStore";

type Person = {
  id: number;
  initials: string;
  name: string;
  company: string;
  email: string;
  dateJoined: string;
  role: string;
  bgColor: string;
};

const roles = ["Media Admin", "Media User", "Client User", "Creative User"];

const peopleData: Person[] = [
  {
    id: 1,
    initials: "RC",
    name: "Ryan Casey",
    company: "Company Name",
    email: "email@companyname.com",
    dateJoined: "06/25",
    role: "Media Admin",
    bgColor: "bg-yellow-400",
  },
  {
    id: 2,
    initials: "RC",
    name: "Ryan Casey",
    company: "Company Name",
    email: "email@companyname.com",
    dateJoined: "06/25",
    role: "Media User",
    bgColor: "bg-red-500",
  },
  {
    id: 3,
    initials: "CB",
    name: "Ryan Casey",
    company: "Company Name",
    email: "email@companyname.com",
    dateJoined: "06/25",
    role: "Client User",
    bgColor: "bg-yellow-600",
  },
  {
    id: 4,
    initials: "DA",
    name: "Ryan Casey",
    company: "Company Name",
    email: "email@companyname.com",
    dateJoined: "06/25",
    role: "Creative User",
    bgColor: "bg-blue-500",
  },
];

export const ManagePeople: React.FC = () => {
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 25;

  // function handleRoleChange(id: number, newRole: string) {
  //   setPeople((prev) =>
  //     prev.map((p) => (p.id === id ? { ...p, role: newRole } : p))
  //   );
  // }

  const { fetchPeople, people, sortField } = useManagePeopleStore(
    useShallow((state) => ({
      fetchPeople: state.fetchPeople,
      people: state.people,
      sortField: state.sortField,
    }))
  );

  const filteredPeople = people?.filter(
    (p) =>
      p?.email?.toLowerCase().includes(search.toLowerCase()) ||
      p?.status?.toLowerCase().includes(search.toLowerCase())
  ) || [];

  // Calculate pagination details
  const totalPages = Math.ceil(filteredPeople.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentPeople = filteredPeople.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  const goPrevious = () => {
    setCurrentPage((page) => Math.max(page - 1, 1));
  };

  const goNext = () => {
    setCurrentPage((page) => Math.min(page + 1, totalPages));
  };

  const handleSort = (field: string) => {
    let newSort: string;
    if (sortField === field) {
      newSort = `-${field}`;
    } else if (sortField === `-${field}`) {
      newSort = '';
    } else {
      newSort = field;
    }
    fetchPeople(newSort || undefined);
  };

  const SortIcon = ({ field }: { field: string }) => {
    if (sortField === field) {
      return (
        <svg className="w-4 h-4 text-pink-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path d="M6 15l6-6 6 6" />
        </svg>
      );
    }
    if (sortField === `-${field}`) {
      return (
        <svg className="w-4 h-4 text-pink-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path d="M6 9l6 6 6-6" />
        </svg>
      );
    }
    return null;
  };

  useEffect(() => {
    fetchPeople();
  }, []);

  return (
    <Layout>
      <div>
        <h1 className="text-2xl font-bold mb-8">Manage People</h1>

        <div className="flex justify-between mb-6">
          <div>
            <TextField
              type="text"
              leadingIcon={<BiSearch />}
              placeholder="Search by keyword, name, etc."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
              className="w-96"
            />
          </div>
          <InviteDialog />
        </div>

        <div className="grid grid-cols-12 border-b border-gray-300 pb-2 mb-2 text-sm text-gray-700 select-none">
          <div className="col-span-3 flex items-center cursor-pointer gap-1" onClick={() => handleSort('email')}>
            Name
            <SortIcon field="email" />
          </div>
          <div className="col-span-2 flex items-center cursor-pointer gap-1" onClick={() => handleSort('email')}>
            Email
            <SortIcon field="email" />
          </div>
          <div className="col-span-2 flex items-center cursor-pointer gap-1" onClick={() => handleSort('team_name')}>
            Team
            <SortIcon field="team_name" />
          </div>
          <div className="col-span-2 flex items-center cursor-pointer gap-1" onClick={() => handleSort('agency_name')}>
            Agency
            <SortIcon field="agency_name" />
          </div>
          <div className="col-span-2 flex items-center cursor-pointer gap-1" onClick={() => handleSort('status')}>
            Status
            <SortIcon field="status" />
          </div>
          <div className="col-span-1"></div>
        </div>

        <div className="divide-y divide-gray-300">
          {currentPeople.map((person) => {
            const color = [
              "bg-red-500",
              "bg-pink-500",
              "bg-purple-500",
              "bg-blue-500",
              "bg-green-500",
              "bg-yellow-500",
              "bg-orange-500",
              "bg-indigo-500",
              "bg-rose-500",
              "bg-teal-500",
              "bg-emerald-500",
            ][Math.floor(person.email.length % 11)];
            return (
              <div
                key={person.id}
                className="grid grid-cols-12 items-center py-3 text-sm"
              >
                <div className="col-span-3 flex items-center gap-3">
                  <div
                    className={`flex items-center justify-center w-12 h-12 rounded-full text-white font-semibold text-lg ${color}`}
                  >
                    {person.email.slice(0, 1).toUpperCase()}
                  </div>
                  <div>{person.email}</div>
                </div>
                <div className="col-span-2 text-gray-600 truncate pr-2">{person.email}</div>
                <div className="col-span-2">
                  {person.team_name ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                      {person.team_name}
                    </span>
                  ) : (
                    <span className="text-gray-400 text-xs">—</span>
                  )}
                </div>
                <div className="col-span-2">
                  {person.media_agency_name ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                      {person.media_agency_name}
                    </span>
                  ) : (
                    <span className="text-gray-400 text-xs">—</span>
                  )}
                </div>
                <div
                  className={`col-span-2 font-semibold ${
                    person.status == "accepted"
                      ? "text-green-500"
                      : person.status == "pending"
                      ? "text-yellow-500"
                      : person.status == "expired"
                      ? "text-red-500"
                      : "text-black"
                  }`}
                >
                  {toTitleCase(person.status)}
                </div>
                <div className="col-span-1 text-xs text-gray-400">{person.role_type ?? ""}</div>
              </div>
            );
          })}
        </div>

        <div className="flex justify-end items-center mt-6 space-x-4 text-sm">
          <Button
            type="secondary"
            onClick={goPrevious}
            disabled={currentPage === 1}
          >
            Previous
          </Button>
          <span>
            Page {currentPage} of {totalPages || 1}
          </span>
          <Button
            type="secondary"
            onClick={goNext}
            disabled={currentPage === totalPages || totalPages === 0}
          >
            Next
          </Button>
        </div>
      </div>
    </Layout>
  );
};

export function InviteDialog() {
  const [isSelected, setIsSelected] = useState<string>(null);
  const [step, setStep] = useState<number>(0);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    message: "",
    role: "",
    role_id: "",
    teams: "",
  });
  const [isLoading, setIsLoading] = useState(false);

  const user_role = useAuthStore((state) => state.user_role);
  const { sendMediaAdminInvitation, sendMediaUserInvitation, getTeams, teams } =
    useManagePeopleStore(
      useShallow((state) => ({
        sendMediaAdminInvitation: state.sendMediaAdminInvitation,
        sendMediaUserInvitation: state.sendMediaUserInvitation,
        getTeams: state.getTeams,
        teams: state.teams,
      }))
    );

  const setError = useGlobalStore((state) => state.setError);

  const dialogRoles =
    user_role == "Global Admin"
      ? [
          {
            id: "0",
            title: "Media Agency",
            description: "",
            color: "bg-[#FF55C0]",
          },
        ]
      : [
          {
            id: "1",
            title: "Coworker",
            description:
              "Your team members can create projects and add users to projects.",
            color: "bg-[#EA33C0]",
          },
          {
            id: "2",
            title: "Client",
            description:
              "A Client can see projects they are added to and review and edit assets.",
            color: "bg-[#D99C10]",
          },
          {
            id: "3",
            title: "Creative Partner",
            description:
              "Creative Partner can see projects they are added to and edit assets.",
            color: "bg-[#5E78D0]",
          },
        ];

  const roles = [
    {
      title: "Creative User",
      value: "804a0c67-1dcc-483d-a80c-c20d97a954fc",
    },
    {
      title: "Media Lead",
      value: "302515ea-b6db-4f3f-913d-86a5813dd558",
    },
    {
      title: "Client User",
      value: "1994a7fd-f799-46c6-b826-d9aa6550b7a9",
    },
    {
      title: "Media Admin",
      value: "9e13b6de-4ab5-4e91-a9c3-c551a1d08a85",
    },
    {
      title: "Media User",
      value: "c8d5cb56-7282-415d-b876-9600bf241471",
    },
  ];

  useEffect(() => {
    getTeams();
  }, []);

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          type="primary"
          trailingIcon={<BsPlus fontSize={25} />}
          onClick={() => {
            setStep(0);
            setIsSelected(null);
            setFormData({
              firstName: "",
              lastName: "",
              email: "",
              message: "",
              role: "",
              role_id: "",
              teams: "",
            });
          }}
        >
          Invite Someone New
        </Button>
      </DialogTrigger>
      {step == 0 && (
        <DialogContent className="sm:max-w-xl bg-white">
          <DialogHeader>
            <DialogTitle className="font-semibold text-xl text-center">
              Invite
            </DialogTitle>
            <DialogDescription className="mt-2">
              {dialogRoles.map((data, index) => (
                <div
                  key={index}
                  className={`border ${
                    isSelected == data.id
                      ? "border-[#EA33C0]"
                      : "border-slate-300"
                  }  rounded-lg text-center p-3 flex flex-col items-center py-6 hover:cursor-pointer my-5`}
                  onClick={() => setIsSelected(data.id)}
                >
                  <div
                    className={`flex items-center justify-center w-12 h-12 rounded-full text-white font-semibold text-lg ${data.color}`}
                  >
                    <BiUser fontSize={30} />
                  </div>
                  <div className="font-semibold text-base mt-4">
                    {data.title}
                  </div>
                  <div className="text-xs mt-1">{data.description}</div>
                </div>
              ))}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex sm:justify-center">
            <Button
              type="secondary"
              onClick={() => {
                if (isSelected != null) {
                  if (isSelected == "2") {
                    const clientRole = roles.find((e) => e.title === "Client User");
                    setFormData((prev) => ({
                      ...prev,
                      role: clientRole!.title,
                      role_id: clientRole!.value,
                    }));
                  } else if (isSelected == "3") {
                    const creativeRole = roles.find((e) => e.title === "Creative User");
                    setFormData((prev) => ({
                      ...prev,
                      role: creativeRole!.title,
                      role_id: creativeRole!.value,
                    }));
                  }
                  setStep(1);
                }
              }}
            >
              Create Invitation
            </Button>
          </DialogFooter>
        </DialogContent>
      )}
      {step == 1 && (
        <DialogContent className="sm:max-w-xl bg-white">
          <DialogHeader>
            <DialogTitle className="font-semibold text-xl text-center">
              Invite a {dialogRoles.find((e) => e.id == isSelected).title}
            </DialogTitle>
            <DialogDescription className="mt-5">
              <div className="flex justify-center">
                <div
                  className={`flex items-center justify-center w-16 h-16 rounded-full text-white font-semibold text-lg ${
                    dialogRoles.find((e) => e.id == isSelected).color
                  }`}
                >
                  <BiUser fontSize={35} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 mt-4">
                <div>
                  <div className="font-semibold mb-1">First Name</div>
                  <TextField
                    type="text"
                    placeholder="First Name"
                    className="w-full"
                    value={formData.firstName}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        firstName: e.target.value,
                      });
                    }}
                  />
                </div>
                <div>
                  <div className="font-semibold mb-1">Last Name</div>
                  <TextField
                    type="text"
                    placeholder="Last Name"
                    className="w-full"
                    value={formData.lastName}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        lastName: e.target.value,
                      });
                    }}
                  />
                </div>
                <div className="col-span-2">
                  <div className="font-semibold mb-1">Email</div>
                  <TextField
                    type="text"
                    placeholder="Email"
                    className="w-full"
                    value={formData.email}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        email: e.target.value,
                      });
                    }}
                  />
                </div>
                {user_role == "Global Admin" && (
                  <div className="col-span-2 mt-2">
                    <TextareaField
                      placeholder="Message"
                      className="w-full"
                      value={formData.message}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          message: e.target.value,
                        });
                      }}
                    />
                  </div>
                )}
                {user_role != "Global Admin" && (
                  <div>
                    <div className="font-semibold mb-1">Role</div>
                    <Dropdown
                      className="w-full"
                      selected={formData.role_id}
                      setSelected={(newValue) => {
                        setFormData({
                          ...formData,
                          role_id: newValue.toString(),
                          role: roles.filter((e) => e.value === newValue)[0]
                            .title,
                        });
                      }}
                      options={
                        isSelected == "1"
                          ? roles.filter((e) =>
                              ["Media Lead", "Media User", "Media Admin"].includes(e.title)
                            )
                          : isSelected == "2"
                          ? roles.filter((e) => e.title === "Client User")
                          : isSelected == "3"
                          ? roles.filter((e) => e.title === "Creative User")
                          : roles
                      }
                      disabled={isSelected == "2" || isSelected == "3"}
                    />
                  </div>
                )}
                {user_role != "Global Admin" && (
                  <div>
                    <div className="font-semibold mb-1">Team</div>
                    <Dropdown
                      className="w-full"
                      selected={formData.teams}
                      setSelected={(newValue) => {
                        setFormData({
                          ...formData,
                          teams: newValue.toString(),
                        });
                      }}
                      options={teams
                        .filter(
                          (team) =>
                            team.team_type ===
                            (isSelected == "2"
                              ? "client"
                              : isSelected == "3"
                              ? "creative"
                              : "media")
                        )
                        .map((team) => ({
                          title: team.name,
                          value: team.id,
                        }))}
                    />
                  </div>
                )}
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex sm:justify-center">
            {isLoading == true ? (
              <Spinner size={30} />
            ) : (
              <Button
                type="primary"
                onClick={async () => {
                  setIsLoading(true);
                  if (user_role == "Global Admin") {
                    try {
                      await sendMediaAdminInvitation({
                        FirstName: formData.firstName,
                        LastName: formData.lastName,
                        email: formData.email,
                        message: formData.message,
                      });
                      setStep(2);
                    } catch (error: any) {
                      setError(error?.message ?? "Something went wrong");
                    } finally {
                      setIsLoading(false);
                    }
                  } else {
                    try {
                      await sendMediaUserInvitation({
                        email: formData.email,
                        firstName: formData.firstName,
                        lastName: formData.lastName,
                        role: formData.role,
                        role_id: formData.role_id,
                        team_id: formData.teams,
                        role_type: formData.role,
                        media_agency_id: teams.find(
                          (e) => e.id === formData.teams
                        ).media_agency_id,
                      });
                      setStep(2);
                    } catch (error: any) {
                      setError(error?.message ?? "Something went wrong");
                    } finally {
                      setIsLoading(false);
                    }
                  }
                }}
                trailingIcon={<BiSend fontSize={20} />}
              >
                Send Invitation
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      )}
      {step == 2 && (
        <DialogContent className="sm:max-w-xl bg-white">
          <DialogHeader>
            <DialogTitle className="font-semibold text-xl text-center">
              Your invitation is in the mail!
            </DialogTitle>
            <DialogDescription className="mt-5">
              <div className="text-center">
                Congrats! We've sent your invitation to {formData.email}. You'll
                receive a new notification when they accept.
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex sm:justify-center mt-3">
            <DialogClose asChild>
              <Button
                type="secondary"
                onClick={() => {
                  setStep(0);
                }}
              >
                Fantastic news!
              </Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      )}
    </Dialog>
  );
}

export default ManagePeople;
